'use client';
import React, { useMemo, useState } from 'react';
import { Box, Collapse, Typography, Tooltip } from '@mui/material';
import { parseRejectionResponse } from 'src/utils/rejectionErrorParser';
import ErrorDetailModal from 'src/components/ErrorDetailModal';

// ---------------------------------------------------------------------------
// Check if a node or any of its descendants have errors
// ---------------------------------------------------------------------------
const hasAnyErrors = (node) => {
  if (node.errors?.length > 0) return true;
  return (node.children || []).some(hasAnyErrors);
};

// ---------------------------------------------------------------------------
// ErrorRow — inline error under a record
// ---------------------------------------------------------------------------
const ErrorRow = ({ error, parentNode, onClick }) => {
  const isPga = error.isPga;
  return (
    <Tooltip title="Click for full description" placement="top-start">
      <Box
        onClick={() => onClick(error, parentNode)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.4,
          cursor: 'pointer',
                    border: '1.5px solid',
          borderColor: 'error.light',
          borderRadius: 5,
          bgcolor: 'error.lighter',
          '&:hover': { filter: 'brightness(0.96)' },
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: 'error.dark', fontWeight: 500, fontSize: '0.78rem' }}
        >
          <strong>{error.code}</strong>: {error.message}
          {isPga && error.pgaCode && (
            <Typography
              component="span"
              variant="caption"
              sx={{ ml: 1, color: 'error.dark', fontStyle: 'italic', opacity: 0.6 }}
            >
              [{error.pgaCode}{error.programCode ? `/${error.programCode}` : ''}]
            </Typography>
          )}
        </Typography>
      </Box>
    </Tooltip>
  );
};

// ---------------------------------------------------------------------------
// RecordNode
// ---------------------------------------------------------------------------
const RecordNode = ({ node, depth = 0, onErrorClick, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const hasChildren = node.children?.length > 0;
  const hasErrors   = node.errors?.length > 0;

    const headerMeta = [
    node.position    && `Position: ${node.position}`,
    node.refId       && `REF ID: ${node.refId}`,
    node.pgaCode     && `PGA Code: ${node.pgaCode}`,
    node.programCode && `Program Code: ${node.programCode}`,
    node.quantity    && `Qty: ${node.quantity}`,
  ]
    .filter(Boolean)
    .join('  ·  ');

    return (
    <Box sx={{ mb: 0.5 }}>
      {/* Header row */}
      <Box
        onClick={() => hasChildren && setExpanded((e) => !e)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.25,
          py: 0.6,
          bgcolor: depth === 0 ? '#f8f8f8' : '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: 0.75,
          cursor: hasChildren ? 'pointer' : 'default',
          '&:hover': hasChildren ? { bgcolor: '#f0f0f0' } : {},
        }}
      >
                {/* Expand indicator */}
        <Box sx={{ width: 14, flexShrink: 0 }}>
          {hasChildren && (
            <Typography variant="caption" sx={{ color: '#999', fontWeight: 700, lineHeight: 1 }}>
              {expanded ? '−' : '+'}
            </Typography>
          )}
        </Box>

        {/* Type badge */}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: 0.8,
            bgcolor: '#546e7a',
            color: '#fff',
            borderRadius: 0.5,
            px: 0.8,
            py: 0.2,
            flexShrink: 0,
          }}
        >
          {node.isGlobal ? 'GLOBAL' : node.type}
        </Typography>

        {/* Meta */}
        {headerMeta && (
          <Typography variant="caption" sx={{ color: '#666', lineHeight: 1.4 }}>
            {headerMeta}
          </Typography>
        )}
      </Box>

                  {/* Errors — indented to align under the badge/title in the header */}
      {hasErrors && (
        <Box sx={{ ml: '42px', mt: 0.5, mb: 0.25, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {node.errors.map((err, i) => (
            <ErrorRow
              key={`${node.id}-err-${i}`}
              error={err}
              parentNode={node}
              onClick={onErrorClick}
            />
          ))}
        </Box>
      )}

                  {/* Children — render ALL children, errors are clickable */}
      {hasChildren && (
        <Collapse in={expanded} timeout={150}>
          <Box sx={{ mt: 0.25, ml: 1, borderLeft: '2px solid #e0e0e0', pl: 0.75 }}>
            {node.children.map((child) => (
              <RecordNode
                key={child.id}
                node={child}
                depth={depth + 1}
                onErrorClick={onErrorClick}
                defaultExpanded={true}
              />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// RejectionErrorTree — main export
// ---------------------------------------------------------------------------
const RejectionErrorTree = ({ responseText }) => {
  const [selectedError, setSelectedError] = useState(null);
  const [selectedContext, setSelectedContext] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const tree = useMemo(
    () => parseRejectionResponse(responseText),
    [responseText]
  );

  const handleErrorClick = (error, parentNode) => {
    setSelectedError(error);
    setSelectedContext(
      parentNode && !parentNode.isGlobal
        ? {
            type: parentNode.type,
            position: parentNode.position,
            refId: parentNode.refId,
          }
        : null
    );
    setModalOpen(true);
  };

  if (!responseText || tree.length === 0) {
    return (
      <Box sx={{ p: 2, color: 'text.secondary' }}>
        <Typography variant="body2">No rejection data to display.</Typography>
      </Box>
    );
  }

  return (
    <Box>
            {/* Tree — all nodes, errors are clickable */}
      <Box>
        {tree.map((node) => (
          <RecordNode
            key={node.id}
            node={node}
            depth={0}
            onErrorClick={handleErrorClick}
            defaultExpanded={true}
          />
        ))}
      </Box>

      {/* Detail Modal */}
      <ErrorDetailModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        error={selectedError}
        context={selectedContext}
      />
    </Box>
  );
};

export default RejectionErrorTree;
